/**
 * Content safety validation utility
 * Story 4.4: Add Timeout and Content Safety
 *
 * Provides pre-generation content safety checks with keyword blocking
 * to maintain family-friendly content.
 *
 * Performance target: <0.5ms per validation
 */

/**
 * Result of content safety validation
 */
export interface ContentSafetyResult {
  /** Whether the content passes safety checks */
  isValid: boolean;
  /** Reason for rejection if not valid */
  reason?: 'inappropriate-content';
}

/**
 * Blocklist of inappropriate words
 * Note: Conservative starter set (10-15 terms) to avoid false positives.
 * Uses whole-word matching to prevent blocking "glass", "class", etc.
 * Expandable post-launch based on abuse patterns.
 */
const BLOCKED_KEYWORDS = [
  'damn',
  'hell',
  'stupid',
  'idiot',
  'dumb',
  'crap',
  'suck',
  'sucks',
  'hate',
  'kill',
  'die',
  'death',
  'bloody',
  'piss',
];

/**
 * Pre-compiled RegExp pattern for content safety validation
 * Performance optimization: Single RegExp created once at module load
 * instead of creating 14 RegExp objects per validation call
 */
const BLOCKED_PATTERN = new RegExp(
  `\\b(${BLOCKED_KEYWORDS.join('|')})\\b`,
  'i'
);

/**
 * Validates content for inappropriate keywords
 *
 * Features:
 * - Case-insensitive matching
 * - Whole-word boundaries (prevents false positives like "glass" containing "ass")
 * - Synchronous operation (no async overhead)
 * - O(1) Set lookup for performance
 *
 * @param prompt - The user input to validate
 * @returns ContentSafetyResult with isValid flag and optional reason
 *
 * @example
 * ```typescript
 * const result = validateContentSafety("dragon volcano");
 * // { isValid: true }
 *
 * const blocked = validateContentSafety("stupid dragon");
 * // { isValid: false, reason: 'inappropriate-content' }
 * ```
 */
export function validateContentSafety(prompt: string): ContentSafetyResult {
  // Empty string is valid (handled by length validation elsewhere)
  if (prompt.length === 0) {
    return { isValid: true };
  }

  // Test against pre-compiled pattern (performance optimized)
  // Pattern uses word boundaries (\b) to match whole words only
  if (BLOCKED_PATTERN.test(prompt)) {
    return {
      isValid: false,
      reason: 'inappropriate-content',
    };
  }

  return { isValid: true };
}
